import { ChevronRight } from "@tamagui/lucide-icons";
import { ReactNode } from "react";
import {
  ButtonProps,
  Circle,
  Heading,
  SizableText,
  Token,
  XStack,
  YStack,
} from "tamagui";
import { getThemedIcon } from "tamagui-config";

import {
  GradientCard,
  GradientCardProps,
  GradientCardThemeProps,
} from "./GradientCard";

export type KPICardThemeProps = Pick<
  GradientCardThemeProps,
  "error" | "info" | "success" | "warning" | "neutral"
>;

export interface KPICardProps extends GradientCardProps {
  icon?: ButtonProps["icon"];
  iconCircleSize?: number;
  title: ReactNode;
  description?: ReactNode;
  isSelected?: boolean;
}

// Decoration diet: the tile surface is always neutral. Status colour lives on
// the icon circle and the numeral, and only when the count is non-zero — a
// red tile shouting "0" carries no information.
export const KPICard = ({
  error,
  info,
  success,
  warning,
  neutral,
  icon,
  iconCircleSize = 56,
  title,
  description,
  isSelected,
  ...rest
}: KPICardProps) => {
  const isZero = title === 0 || title === "0";
  // A pressable tile that is not a filter toggle (no isSelected) navigates
  // somewhere — say so with a chevron.
  const navigates = !!rest.onPress && isSelected === undefined;
  const { iconBackground, iconColor, accent } = isZero
    ? MUTED
    : getThemeTokens({ error, info, success, warning, neutral });

  const themedIcon = icon
    ? getThemedIcon(icon, {
        size: "$2.5",
        color: iconColor,
      })
    : undefined;

  return (
    <GradientCard
      error={error}
      info={info}
      success={success}
      warning={warning}
      neutral={neutral}
      isSelected={isSelected}
      backgroundColor="$transparentBorder"
      backgroundImage="none"
      flex={1}
      {...(rest.onPress
        ? { role: "button", tabIndex: 0, cursor: "pointer" }
        : {})}
      {...rest}
    >
      <XStack gap="$3.5" alignItems="center">
        {themedIcon && (
          <Circle size={iconCircleSize} backgroundColor={iconBackground}>
            {themedIcon}
          </Circle>
        )}
        <YStack gap="$1.5" flex={1} justifyContent="center">
          {typeof title === "string" || typeof title === "number" ? (
            <Heading size="$14" lineHeight="$7"
              fontSize="$8"
              color={accent}
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {title}
            </Heading>
          ) : (
            title
          )}
          {typeof description === "string" ||
          typeof description === "number" ? (
            <SizableText
              size="$4"
              color="$white10"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {description}
            </SizableText>
          ) : (
            description
          )}
        </YStack>
        {navigates && <ChevronRight size="$1" color="$white10" />}
      </XStack>
    </GradientCard>
  );
};

type KPITokens = {
  iconBackground: Token;
  iconColor: Token;
  accent: Token;
};

const MUTED: KPITokens = {
  iconBackground: "$transparentBorder" as Token,
  iconColor: "$white10" as Token,
  accent: "$white10" as Token,
};

const getThemeTokens = ({
  error,
  info,
  success,
  warning,
  neutral,
}: KPICardThemeProps): KPITokens => {
  if (error) {
    return {
      iconBackground: "$statusCritical" as Token,
      iconColor: "$black12" as Token,
      accent: "$statusCritical" as Token,
    };
  }

  if (info) {
    return {
      iconBackground: "$info" as Token,
      iconColor: "$black12" as Token,
      accent: "$info" as Token,
    };
  }

  if (success) {
    return {
      iconBackground: "$statusGood" as Token,
      iconColor: "$black12" as Token,
      accent: "$statusGood" as Token,
    };
  }

  if (warning) {
    return {
      iconBackground: "$statusWarning" as Token,
      iconColor: "$black12" as Token,
      accent: "$statusWarning" as Token,
    };
  }

  if (neutral) {
    return {
      iconBackground: "$neutral" as Token,
      iconColor: "$white12" as Token,
      accent: "$white12" as Token,
    };
  }

  return {
    iconBackground: "$transparentBorder" as Token,
    iconColor: "$white12" as Token,
    accent: "$white12" as Token,
  };
};
